import { useState } from "react"
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
  Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography,
} from "@mui/material"
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { z } from "zod"
import { schemas } from "../generated/api/client"

// DBS-248: the DBS Status column — a one-or-two-word summary of the latest
// scraped tracking-service state, plus an info button opening the full
// observation history for the application.

type DBSTracking = z.infer<typeof schemas.DBSTracking>
type DBSTrackingEntry = DBSTracking['latest']

// Days when under 100 days old, calendar months beyond that (never years)
const timeAgo = (isoDate: string): string => {
  const then = new Date(isoDate)
  const now = new Date()
  const days = Math.floor((now.getTime() - then.getTime()) / (24 * 60 * 60 * 1000))
  if (days < 100) {
    return days === 0 ? 'today' : days === 1 ? '1 day ago' : `${days} days ago`
  }
  let months = (now.getFullYear() - then.getFullYear()) * 12 + (now.getMonth() - then.getMonth())
  if (now.getDate() < then.getDate()) months--
  return months === 1 ? '1 month ago' : `${months} months ago`
}

const dateTime = (iso: string): string => new Date(iso).toLocaleString()

const statusLabel = (entry: DBSTrackingEntry): string =>
  entry.type === 'stages'
    ? (entry.current_stage != null ? `Stage ${entry.current_stage}/5` : 'In progress')
    : { withdrawn: 'Withdrawn', dispatched: 'Dispatched', not_found: 'Not found', unrecognised: 'Unreadable' }[entry.type]

const STAGE_STATE_LABELS: Record<string, string> = {
  in_progress: 'in progress',
  not_started: 'not started',
  not_required: 'not required',
}

const entryDetails = (entry: DBSTrackingEntry): string => {
  switch (entry.type) {
    case 'stages':
      return (entry.stages ?? [])
        .map(stage => `S${stage.n} ${stage.completed_on != null ? `✓ ${stage.completed_on}` : STAGE_STATE_LABELS[stage.state] ?? stage.state}`)
        .join(' · ')
    case 'withdrawn':
      return entry.event_date != null
        ? `Withdrawn on ${entry.event_date} (${timeAgo(entry.event_date)})${entry.received_on != null ? `; received by DBS ${entry.received_on}` : ''}`
        : 'Withdrawn from the DBS process'
    case 'dispatched':
      return entry.event_date != null
        ? `Certificate dispatched on ${entry.event_date} (${timeAgo(entry.event_date)})`
        : 'Certificate dispatched'
    case 'not_found':
      return 'The tracking service has no record of this application'
    case 'unrecognised':
      return 'The tracking page could not be understood'
  }
}

type DBSTrackingStatusCellProps = {
  tracking: DBSTracking | undefined,
  dbs_enumber: string | undefined,
}

const DBSTrackingStatusCell: React.FC<DBSTrackingStatusCellProps> = ({ tracking, dbs_enumber }) => {
  const [open, setOpen] = useState(false)
  if (tracking == null) return null

  const latest = tracking.latest
  // Newest observation first
  const entries = [latest, ...[...tracking.history].reverse()]

  return <>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Tooltip title={`DBS tracking: ${statusLabel(latest)} · checked ${dateTime(latest.last_seen_at)}`}>
        <Typography variant="body2" color={latest.type === 'withdrawn' ? 'warning.main' : 'text.primary'}>
          {statusLabel(latest)}
        </Typography>
      </Tooltip>
      <IconButton size="small" aria-label="DBS tracking details" onClick={() => setOpen(true)}>
        <InfoOutlinedIcon fontSize="inherit" />
      </IconButton>
    </Box>

    <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle>DBS Tracking{dbs_enumber != null ? ` — ${dbs_enumber}` : ''}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ marginBottom: 2 }}>
          Last checked {dateTime(latest.last_seen_at)} ({timeAgo(latest.last_seen_at)})
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Status</TableCell>
              <TableCell>Details</TableCell>
              <TableCell>First Seen</TableCell>
              <TableCell>Last Confirmed</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map((entry, index) => (
              <TableRow key={`${entry.first_seen_at}-${index}`}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Typography variant="body2" color={entry.type === 'withdrawn' ? 'warning.main' : 'text.primary'}>
                    {statusLabel(entry)}{index === 0 ? ' (current)' : ''}
                  </Typography>
                </TableCell>
                <TableCell>{entryDetails(entry)}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{dateTime(entry.first_seen_at)}<br />({timeAgo(entry.first_seen_at)})</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{dateTime(entry.last_seen_at)}<br />({timeAgo(entry.last_seen_at)})</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)}>Close</Button>
      </DialogActions>
    </Dialog>
  </>
}

export default DBSTrackingStatusCell
